import React, { useState, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";

// Import react-pdf CSS for text layer and annotation layer
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

// Configure PDF.js worker using unpkg CDN
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PDFViewerProps {
  pdfBlob?: Blob | null;
  pdfUrl?: string | null;
  className?: string;
  onRendered?: () => void; // Called when the PDF has been parsed and is about to render
}

/**
 * PDF Viewer component for displaying compiled Typst documents.
 *
 * Features:
 * - Renders all pages of a PDF document from direct URL or local Blob
 * - Handles loading and error states
 * - Automatically cleans up object URLs to prevent memory leaks
 * - Responsive width (fits container)
 */
class PDFViewerErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("PDFViewer caught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-full min-h-[400px] w-full flex-col items-center justify-center rounded-lg border border-dashed border-red-200 bg-red-50 p-8">
          <div className="text-center text-destructive">
            <p className="text-lg font-medium">PDF Renderer Error</p>
            <p className="mt-2 max-w-md text-center text-sm">
              The PDF viewer encountered an unexpected error while trying to render the document.
            </p>
            <div className="mt-4 max-w-full overflow-auto rounded bg-red-100/50 p-3 text-left font-mono text-xs">
              {this.state.error?.message || "Unknown error"}
            </div>
            <button
              onClick={() => this.setState({ hasError: false, error: null })}
              className="mt-4 rounded-md bg-red-100 px-4 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-200"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export function PDFViewer({
  pdfBlob,
  pdfUrl: directPdfUrl,
  className = "",
  onRendered,
}: PDFViewerProps) {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Convert blob or direct URL to renderable pdfUrl
  useEffect(() => {
    if (directPdfUrl) {
      console.log("[PDFViewer] Using direct server PDF URL:", directPdfUrl);
      setPdfUrl(directPdfUrl);
      setError(null);
      return;
    }

    console.log("[PDFViewer] pdfBlob changed:", {
      hasPdfBlob: !!pdfBlob,
      pdfBlobSize: pdfBlob?.size,
      pdfBlobType: pdfBlob?.type,
    });

    if (!pdfBlob) {
      console.log("[PDFViewer] No pdfBlob or directPdfUrl, clearing URL");
      setPdfUrl(null);
      setNumPages(null);
      setError(null);
      return;
    }

    // Create object URL from blob
    const url = URL.createObjectURL(pdfBlob);
    console.log("[PDFViewer] Created object URL from blob:", url);
    setPdfUrl(url);
    setError(null);

    // Cleanup: revoke object URL when component unmounts or blob changes
    return () => {
      console.log("[PDFViewer] Revoking object URL (delayed):", url);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    };
  }, [pdfBlob, directPdfUrl]);

  /**
   * Called when PDF document loads successfully
   */
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setError(null);
    onRendered?.(); // Signal to parent that PDF is now rendered
  };

  /**
   * Called when PDF document fails to load
   */
  const onDocumentLoadError = (error: Error) => {
    console.error("PDF load error:", error);
    setError("Failed to load PDF document. Please try again.");
    setNumPages(null);
  };

  // No PDF to display
  if (!pdfUrl) {
    console.log('[PDFViewer] Rendering "No document to display" state');
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className="text-center text-muted-foreground">
          <p className="text-sm">No document to display</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className="text-center text-destructive">
          <p className="text-sm font-medium">Error Loading PDF</p>
          <p className="mt-2 text-xs">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <PDFViewerErrorBoundary>
      <div className={`pdf-viewer-container ${className}`}>
        <Document
          key={pdfUrl}
          file={pdfUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={
            <div className="flex items-center justify-center p-8">
              <div className="text-center">
                <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
                <p className="text-sm text-muted-foreground">Loading PDF...</p>
              </div>
            </div>
          }
          error={
            <div className="flex items-center justify-center p-8">
              <div className="text-center text-destructive">
                <p className="text-sm font-medium">Failed to load PDF</p>
                <p className="mt-2 text-xs">Please try refreshing the page</p>
              </div>
            </div>
          }
          className="flex flex-col items-center gap-4"
        >
          {numPages &&
            Array.from(new Array(numPages), (_, index) => (
              <div
                key={`page_${index + 1}`}
                className="overflow-hidden rounded-sm border border-border shadow-lg"
              >
                <Page
                  pageNumber={index + 1}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                  className="max-w-full"
                  width={800} // Fixed width for consistent rendering
                />
              </div>
            ))}
        </Document>
      </div>
    </PDFViewerErrorBoundary>
  );
}
